import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { OrderFields } from "../../../constants";
import useOrderFormik from "../../../hooks/useOrderFormik";

type THasAddressValuesChangedReturn = {
  hasAddressValuesChanged: boolean;
  resetChangedState: () => void;
};

// This hook checks if there is address selected from address book
// and if there were any unsaved changes to any field of that selected address

const getAddressCompareValue = (addressValues: any[]): string => addressValues.join(",");

const useHasAddressValuesChanged = (): THasAddressValuesChangedReturn => {
  const [hasAddressValuesChanged, setHasAddressValuesChanged] = useState(false);

  const prevAddressIdRef = useRef<string | null>(null);
  const initialCompareValueRef = useRef<string | null>(null);

  const { values } = useOrderFormik();

  const {
    [OrderFields.RECEIVER_ADDRESS_ID]: addressId,
    [OrderFields.RECEIVER_NAME]: name,
    [OrderFields.RECEIVER_PHONE_NUMBER]: phoneNumber,
    [OrderFields.RECEIVER_EMAIL]: email,
    [OrderFields.RECEIVER_LOCKER]: locker,
    [OrderFields.RECEIVER_STREET]: street,
    [OrderFields.RECEIVER_HOUSE_NO]: houseNo,
    [OrderFields.RECEIVER_FLAT_NO]: flatNo,
    [OrderFields.RECEIVER_CITY]: city,
    [OrderFields.RECEIVER_ZIP_CODE]: zipCode,
  } = values;

  const resetChangedState = useCallback(() => {
    setHasAddressValuesChanged(false);
    initialCompareValueRef.current = addressId
      ? getAddressCompareValue([name, phoneNumber, email, locker, street, houseNo, flatNo, city, zipCode])
      : null;
  }, [addressId, city, email, flatNo, houseNo, locker, name, phoneNumber, street, zipCode]);

  // Reset state if another address is selected
  useEffect(() => {
    if (prevAddressIdRef.current === addressId) {
      return;
    }
    prevAddressIdRef.current = addressId || null;
    resetChangedState();
  }, [addressId, resetChangedState]);

  const currentCompareValue = useMemo(
    () => getAddressCompareValue([name, phoneNumber, email, locker, street, houseNo, flatNo, city, zipCode]),
    [city, email, flatNo, houseNo, locker, name, phoneNumber, street, zipCode]
  );

  useEffect(() => {
    if (!addressId) {
      return;
    }
    setHasAddressValuesChanged(initialCompareValueRef.current !== currentCompareValue);
  }, [addressId, currentCompareValue]);

  return { hasAddressValuesChanged, resetChangedState };
};
export default useHasAddressValuesChanged;
